import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { AdminShell } from "@/pages/admin/Dashboard";
import ImageUpload from "@/pages/admin/ImageUpload";

const EMPTY = {
    slug: "",
    title: "",
    kicker: "",
    excerpt: "",
    image: "",
    readTime: "5 min de lecture",
    author: "Joel Kotho",
    author_photo: "/brand/joel-kotho-avatar.jpg",
    author_role: "Fondateur & Managing Partner",
    body: [""],
    published: true,
    order: 0,
};

export default function ArticleEditor() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isNew = !id;
    const [form, setForm] = useState(EMPTY);
    const [loading, setLoading] = useState(!isNew);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (isNew) return;
        api.get("/admin/articles")
            .then(({ data }) => {
                const a = data.find((x) => x.id === id);
                if (a) {
                    setForm({
                        slug: a.slug,
                        title: a.title,
                        kicker: a.kicker,
                        excerpt: a.excerpt,
                        image: a.image,
                        readTime: a.readTime,
                        author: a.author || "Joel Kotho",
                        author_photo: a.author_photo || "/brand/joel-kotho-avatar.jpg",
                        author_role: a.author_role || "Fondateur & Managing Partner",
                        body: a.body && a.body.length ? a.body : [""],
                        published: a.published,
                        order: a.order || 0,
                    });
                }
            })
            .finally(() => setLoading(false));
    }, [id, isNew]);

    const updateBody = (i, value) =>
        setForm({
            ...form,
            body: form.body.map((p, idx) => (idx === i ? value : p)),
        });

    const addPara = () => setForm({ ...form, body: [...form.body, ""] });
    const removePara = (i) =>
        setForm({ ...form, body: form.body.filter((_, idx) => idx !== i) });

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError(null);
        const payload = {
            ...form,
            body: form.body.filter((p) => p.trim() !== ""),
        };
        try {
            if (isNew) await api.post("/admin/articles", payload);
            else await api.put(`/admin/articles/${id}`, payload);
            navigate("/admin");
        } catch (err) {
            setError(err?.response?.data?.detail || "Erreur");
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <AdminShell>Chargement…</AdminShell>;

    return (
        <AdminShell>
            <div data-testid="article-editor" className="max-w-3xl">
                <button
                    onClick={() => navigate("/admin")}
                    className="inline-flex items-center gap-3 text-xv-midnight text-[11px] tracking-[0.24em] uppercase mb-8"
                >
                    <ArrowLeft strokeWidth={1.5} size={14} /> Retour
                </button>

                <h1
                    className="font-serif text-4xl md:text-5xl text-xv-black mb-10"
                    style={{ fontWeight: 400 }}
                >
                    {isNew ? "Nouvel article" : "Modifier l'article"}
                </h1>

                <form onSubmit={save} className="space-y-8">
                    {error && (
                        <div className="border-l-4 border-red-700 bg-red-50 p-4 text-red-700 text-sm">
                            {error}
                        </div>
                    )}

                    <Field label="Titre" required>
                        <input
                            data-testid="ae-title"
                            className="xv-input"
                            value={form.title}
                            onChange={(e) =>
                                setForm({ ...form, title: e.target.value })
                            }
                            required
                        />
                    </Field>

                    <div className="grid md:grid-cols-2 gap-8">
                        <Field label="Slug (URL)" required>
                            <input
                                data-testid="ae-slug"
                                className="xv-input"
                                value={form.slug}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        slug: e.target.value
                                            .toLowerCase()
                                            .replace(/[^a-z0-9-]+/g, "-")
                                            .replace(/^-|-$/g, ""),
                                    })
                                }
                                required
                            />
                        </Field>
                        <Field label="Kicker">
                            <input
                                data-testid="ae-kicker"
                                className="xv-input"
                                value={form.kicker}
                                onChange={(e) =>
                                    setForm({ ...form, kicker: e.target.value })
                                }
                            />
                        </Field>
                    </div>

                    <Field label="Résumé (excerpt)">
                        <textarea
                            data-testid="ae-excerpt"
                            className="xv-input"
                            rows={3}
                            value={form.excerpt}
                            onChange={(e) =>
                                setForm({ ...form, excerpt: e.target.value })
                            }
                        />
                    </Field>

                    <div className="grid md:grid-cols-2 gap-8">
                        <ImageUpload
                            label="Image principale de l'article"
                            value={form.image}
                            onChange={(v) => setForm({ ...form, image: v })}
                            testid="ae-image"
                        />
                        <Field label="Temps de lecture">
                            <input
                                data-testid="ae-readtime"
                                className="xv-input"
                                value={form.readTime}
                                onChange={(e) =>
                                    setForm({ ...form, readTime: e.target.value })
                                }
                            />
                        </Field>
                    </div>

                    {/* Author block — name, role, photo */}
                    <div className="pt-6 border-t border-xv-line space-y-6">
                        <div className="grid md:grid-cols-2 gap-6">
                            <Field label="Nom de l'auteur">
                                <input
                                    data-testid="ae-author"
                                    className="xv-input"
                                    value={form.author || ""}
                                    onChange={(e) =>
                                        setForm({ ...form, author: e.target.value })
                                    }
                                />
                            </Field>
                            <Field label="Fonction / titre de l'auteur">
                                <input
                                    data-testid="ae-author-role"
                                    className="xv-input"
                                    value={form.author_role || ""}
                                    onChange={(e) =>
                                        setForm({ ...form, author_role: e.target.value })
                                    }
                                />
                            </Field>
                        </div>
                        <ImageUpload
                            label="Photo de l'auteur (carré 1:1 recommandé)"
                            value={form.author_photo || ""}
                            onChange={(v) => setForm({ ...form, author_photo: v })}
                            testid="ae-author-photo"
                        />
                    </div>

                    <Field label="Corps de l'article">
                        <div className="space-y-4">
                            {form.body.map((p, i) => (
                                <div key={i} className="flex gap-3">
                                    <textarea
                                        data-testid={`ae-body-${i}`}
                                        className="xv-input flex-1"
                                        rows={4}
                                        value={p}
                                        onChange={(e) =>
                                            updateBody(i, e.target.value)
                                        }
                                        placeholder={`Paragraphe ${i + 1}`}
                                    />
                                    {form.body.length > 1 && (
                                        <button
                                            type="button"
                                            onClick={() => removePara(i)}
                                            className="self-start text-xs text-red-700 underline mt-2"
                                        >
                                            Retirer
                                        </button>
                                    )}
                                </div>
                            ))}
                            <button
                                type="button"
                                onClick={addPara}
                                className="text-[11px] tracking-[0.24em] uppercase text-xv-midnight underline"
                            >
                                + Ajouter un paragraphe
                            </button>
                        </div>
                    </Field>

                    <div className="grid md:grid-cols-2 gap-8">
                        <Field label="Ordre d'affichage">
                            <input
                                type="number"
                                className="xv-input"
                                value={form.order}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        order: parseInt(e.target.value || "0", 10),
                                    })
                                }
                            />
                        </Field>
                        <Field label="Publié">
                            <label className="inline-flex items-center gap-3 mt-3">
                                <input
                                    type="checkbox"
                                    data-testid="ae-published"
                                    checked={form.published}
                                    onChange={(e) =>
                                        setForm({
                                            ...form,
                                            published: e.target.checked,
                                        })
                                    }
                                    className="w-4 h-4 accent-xv-midnight"
                                />
                                <span className="text-sm font-light">
                                    Visible sur le site public
                                </span>
                            </label>
                        </Field>
                    </div>

                    <button
                        type="submit"
                        data-testid="ae-save"
                        disabled={saving}
                        className="inline-flex items-center gap-4 px-10 py-4 bg-xv-midnight text-xv-ivory text-[11px] tracking-[0.3em] uppercase hover:bg-xv-black transition disabled:opacity-60"
                    >
                        {saving ? "Enregistrement…" : "Enregistrer"}
                    </button>
                </form>
            </div>
        </AdminShell>
    );
}

function Field({ label, required, children }) {
    return (
        <div>
            <label className="overline text-xv-gold block mb-3">
                {label}
                {required && " *"}
            </label>
            {children}
        </div>
    );
}
